import posthog, { type CaptureResult, type PostHogConfig } from 'posthog-js';

const projectToken = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN;
const apiHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

const hostname = typeof window === 'undefined' ? '' : window.location.hostname;
const hasConfig = Boolean(projectToken && apiHost);
const shouldTrack = hasConfig && hostname === 'dashboard.eye-able.com';

let isTrackingEnabled = false;

/**
 * The complexity around tracking enabled state and dropping events is based on
 * the `autocapture` and `persistence` settings: After init, which lives outside
 * the React lifecycle, Posthog will already capture events and assign them to
 * anonymous users, thereby creating noise in our Activity log. Changing one or
 * both these settings will likely allow us to simplify this setup.
 */

if (shouldTrack) {
  const config: Partial<PostHogConfig> = {
    api_host: apiHost,
    persistence: 'memory',
    defaults: '2026-01-30',
    autocapture: true,
    session_recording: {
      maskAllInputs: true,
    },
    before_send: (event: CaptureResult | null): CaptureResult | null => {
      if (shouldDropEvent(event)) {
        return null;
      }

      return event;
    },
  };

  posthog.init(projectToken!, config);
}

function shouldDropEvent(event: CaptureResult | null): boolean {
  if (!event) {
    return true;
  }

  const pathname =
    typeof event.properties?.$pathname === 'string'
      ? event.properties.$pathname
      : undefined;

  return (
    !isTrackingEnabled || pathname?.startsWith('/authentication/') === true
  );
}

export function enablePostHogTracking(): boolean {
  if (!shouldTrack || isTrackingEnabled) {
    return false;
  }

  isTrackingEnabled = true;

  posthog.startSessionRecording();

  return true;
}

export function disablePostHogTracking({
  reset = false,
}: { reset?: boolean } = {}): void {
  if (isTrackingEnabled) {
    posthog.stopSessionRecording();
  }

  if (reset) {
    posthog.reset(true);
  }

  isTrackingEnabled = false;
}

export default posthog;
